import type { AssessmentResult as Assessment, DeviceType, PriceData, TradeInQuote } from "@pandas/http-client";
import { StateCreator } from "zustand";
import { getAssessmentStorageKey } from "../../utils/assessment/display";
import { Storage } from "../index";

// INITIAL STATE
export type DeviceInfoSlice = {
	deviceInfo: {
		imei?: string;
		category?: DeviceType;
		brand?: string;
		model?: string;
		modelName?: string;
		productId?: string;
		capacity?: string;
		color?: string;
		condition?: string;
		fmiOn?: boolean;
		manufacturer?: string;
		description?: string;
		deviceGrade?: string;
	};
	priceInfo: PriceData | null;
	selectedPromoCode: string | undefined;
	quote: TradeInQuote | null;
	assessments: Assessment[];
	hasCosmeticTests: boolean;
	hasFunctionalTests: boolean;
	setDeviceInfo: (_deviceInfo: Partial<DeviceInfoPayload>) => void;
	setAssessments: (_assessments: Assessment[], _hasCosmeticTests: boolean, _hasFunctionalTests: boolean) => void;
	setPriceInfo: (_priceInfo: PriceData | null) => void;
	setSelectedPromoCode: (_code: string | undefined) => void;
	setQuote: (_quote: TradeInQuote | null) => void;
	resetDeviceInfo: () => void;
};

// ACTION PAYLOAD TYPES
type DeviceInfoPayload = {
	imei: string;
	category: DeviceType;
	brand: string;
	model: string;
	modelName: string;
	productId: string;
	capacity: string;
	color: string;
	condition: string;
	fmiOn: boolean;
	manufacturer: string;
	deviceGrade?: string;
	description: string;
};

function mergeAssessments(currentAssessments: Assessment[], nextAssessments: Assessment[]) {
	const mergedAssessments = new Map<string, Assessment>();

	for (const assessment of [...currentAssessments, ...nextAssessments]) {
		mergedAssessments.set(getAssessmentStorageKey(assessment), assessment);
	}

	return Array.from(mergedAssessments.values());
}

const initialState = {
	deviceInfo: {},
	hasCosmeticTests: false,
	hasFunctionalTests: false,
	priceInfo: null,
	selectedPromoCode: undefined,
	assessments: [],
	quote: null,
};

export const createDeviceInfoSlice: StateCreator<Storage, [], [], DeviceInfoSlice> = (set) => ({
	...initialState,
	setDeviceInfo: (deviceInfoPayload: Partial<DeviceInfoPayload>) =>
		set((state) => ({
			deviceInfo: {
				...state.deviceInfo,
				...deviceInfoPayload,
			},
		})),
	setAssessments: (assessments: Assessment[], hasCosmeticTests: boolean, hasFunctionalTests: boolean) =>
		set((state) => ({
			assessments: mergeAssessments(state.assessments, assessments),
			hasCosmeticTests,
			hasFunctionalTests,
		})),
	setPriceInfo: (priceInfo: PriceData | null) =>
		set(() => ({
			priceInfo,
			selectedPromoCode: undefined,
		})),
	setSelectedPromoCode: (code: string | undefined) =>
		set(() => ({
			selectedPromoCode: code,
		})),
	setQuote: (quote: TradeInQuote | null) =>
		set(() => ({
			quote,
		})),
	resetDeviceInfo: () =>
		set(() => ({
			...initialState,
		})),
});
